<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Company Payments</h1>
            <p class="text-slate-400">Manage company payments, invoices, and financial transactions</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="showCreateInvoiceModal = true" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              Create Invoice
            </button>
            <button @click="exportPaymentData" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Export Data
            </button>
          </div>
        </div>
      </div>

      <!-- Payment Stats -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Total Revenue</p>
            <p class="text-3xl font-bold text-white">${{ paymentStats.totalRevenue.toLocaleString() }}</p>
            <p class="text-sm text-emerald-400">this month</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Pending Payments</p>
            <p class="text-3xl font-bold text-white">${{ paymentStats.pendingPayments.toLocaleString() }}</p>
            <p class="text-sm text-cyan-400">outstanding</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Overdue Invoices</p>
            <p class="text-3xl font-bold text-white">{{ paymentStats.overdueInvoices }}</p>
            <p class="text-sm text-amber-400">past due</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Payment Rate</p>
            <p class="text-3xl font-bold text-white">{{ paymentStats.paymentRate }}%</p>
            <p class="text-sm text-purple-400">on time</p>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mb-8">
        <h2 class="text-xl font-semibold text-white mb-4">Quick Actions</h2>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button @click="showCreateInvoiceModal = true" class="quick-action-card">
            <svg class="w-8 h-8 text-emerald-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <span class="text-sm font-medium text-white">Create Invoice</span>
          </button>
          
          <button @click="showPaymentModal = true" class="quick-action-card">
            <svg class="w-8 h-8 text-cyan-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
            </svg>
            <span class="text-sm font-medium text-white">Record Payment</span>
          </button>
          
          <button @click="generatePaymentReport" class="quick-action-card">
            <svg class="w-8 h-8 text-amber-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <span class="text-sm font-medium text-white">Generate Report</span>
          </button>
          
          <button @click="sendPaymentReminders" class="quick-action-card">
            <svg class="w-8 h-8 text-purple-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <span class="text-sm font-medium text-white">Send Reminders</span>
          </button>
        </div>
      </div>

      <!-- Payment Overview -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Recent Payments -->
        <div class="lg:col-span-2">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-xl font-semibold text-white">Recent Payments</h2>
              <div class="flex items-center space-x-2">
                <input 
                  v-model="searchTerm" 
                  type="text" 
                  placeholder="Search payments..."
                  class="form-input w-48"
                />
                <select v-model="filterStatus" class="form-select">
                  <option value="">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>

            <div class="space-y-4">
              <div v-for="payment in filteredPayments" :key="payment.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
                <div class="flex items-center space-x-4">
                  <div class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                    <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-sm font-medium text-white">{{ payment.description }}</h3>
                    <p class="text-xs text-slate-400">{{ payment.client }} • {{ formatDate(payment.date) }}</p>
                  </div>
                </div>
                <div class="flex items-center space-x-4">
                  <div class="text-right">
                    <p class="text-sm font-medium text-white">${{ payment.amount.toLocaleString() }}</p>
                    <p class="text-xs text-slate-400">{{ payment.paymentMethod }}</p>
                  </div>
                  <span :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    payment.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                    payment.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  ]">
                    {{ payment.status }}
                  </span>
                  <div class="flex items-center space-x-2">
                    <button @click="viewPaymentDetails(payment)" class="text-emerald-400 hover:text-emerald-300">
                      View
                    </button>
                    <button @click="downloadReceipt(payment.id)" class="text-cyan-400 hover:text-cyan-300">
                      Receipt
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Summary -->
        <div class="lg:col-span-1">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Payment Summary</h3>
            
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Total Received</span>
                <span class="text-sm font-medium text-white">${{ paymentSummary.totalReceived.toLocaleString() }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Pending</span>
                <span class="text-sm font-medium text-amber-400">${{ paymentSummary.pending.toLocaleString() }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Overdue</span>
                <span class="text-sm font-medium text-red-400">${{ paymentSummary.overdue.toLocaleString() }}</span>
              </div>
              <div class="border-t border-slate-700/50 pt-4">
                <div class="flex items-center justify-between">
                  <span class="text-lg font-semibold text-white">Net Revenue</span>
                  <span class="text-lg font-bold text-emerald-400">${{ paymentSummary.netRevenue.toLocaleString() }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Invoices -->
      <div class="mt-8">
        <h2 class="text-xl font-semibold text-white mb-6">Recent Invoices</h2>
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Invoice #</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Client</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Amount</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Due Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="invoice in recentInvoices" :key="invoice.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ invoice.invoiceNumber }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white">{{ invoice.client }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">${{ invoice.amount.toLocaleString() }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ formatDate(invoice.dueDate) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      invoice.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                      invoice.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    ]">
                      {{ invoice.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center space-x-2">
                      <button @click="viewInvoiceDetails(invoice)" class="text-emerald-400 hover:text-emerald-300">
                        View
                      </button>
                      <button @click="downloadInvoice(invoice.id)" class="text-cyan-400 hover:text-cyan-300">
                        Download
                      </button>
                      <button @click="sendInvoiceReminder(invoice.id)" class="text-amber-400 hover:text-amber-300">
                        Remind
                      </button>
                      <button
                        v-if="invoice.status !== 'paid' && invoice.status !== 'cancelled'"
                        @click="voidInvoice(invoice)"
                        class="text-red-400 hover:text-red-300"
                      >Void</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-if="viewingInvoice" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 invoice-view">
        <div class="w-full max-w-3xl">
          <div class="flex justify-end gap-2 mb-3 no-print">
            <button type="button" class="btn-secondary" @click="viewingInvoice = null">Close</button>
            <button type="button" class="btn-primary" @click="printViewingInvoice">Print / PDF</button>
          </div>
          <InvoiceDocument :invoice="viewingInvoice" :issuer="companyIssuer" preview />
        </div>
      </div>

      <!-- Create Invoice Modal -->
      <div v-if="showCreateInvoiceModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Create Invoice</h3>
          
          <form @submit.prevent="submitInvoice" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Client Name</label>
                <input v-model="invoiceData.clientName" type="text" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Invoice Amount</label>
                <input v-model="invoiceData.amount" type="number" step="0.01" class="form-input w-full" required />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <input v-model="invoiceData.description" type="text" class="form-input w-full" required />
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Due Date</label>
                <input v-model="invoiceData.dueDate" type="date" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Payment Terms</label>
                <select v-model="invoiceData.paymentTerms" class="form-select w-full" required>
                  <option value="">Select Terms</option>
                  <option value="net-30">Net 30</option>
                  <option value="net-15">Net 15</option>
                  <option value="due-on-receipt">Due on Receipt</option>
                </select>
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Notes</label>
              <textarea v-model="invoiceData.notes" class="form-input w-full" rows="3"></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showCreateInvoiceModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Create Invoice
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Record Payment Modal -->
      <div v-if="showPaymentModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Record Payment</h3>
          
          <form @submit.prevent="submitPayment" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Payment Amount</label>
                <input v-model="paymentData.amount" type="number" step="0.01" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Payment Method</label>
                <select v-model="paymentData.paymentMethod" class="form-select w-full" required>
                  <option value="">Select Method</option>
                  <option value="bank-transfer">Bank Transfer</option>
                  <option value="check">Check</option>
                  <option value="credit-card">Credit Card</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Client Name</label>
              <input v-model="paymentData.clientName" type="text" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <input v-model="paymentData.description" type="text" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Payment Date</label>
              <input v-model="paymentData.paymentDate" type="date" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Reference Number</label>
              <input v-model="paymentData.referenceNumber" type="text" class="form-input w-full" />
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showPaymentModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Record Payment
              </button>
            </div>
          </form>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import InvoiceDocument from '@/components/invoices/InvoiceDocument.vue'

const userStore = useUserStore()

// Reactive data
const showCreateInvoiceModal = ref(false)
const showPaymentModal = ref(false)
const viewingInvoice = ref<any | null>(null)
const companyIssuer = computed(() => ({
  name: userStore.currentUser?.company || userStore.currentUser?.name || 'Company',
  email: userStore.currentUser?.email || '',
}))
const searchTerm = ref('')
const filterStatus = ref('')

const paymentStats = ref({
  totalRevenue: 0,
  pendingPayments: 0,
  overdueInvoices: 0,
  paymentRate: 0
})

const payments = ref([])
const recentInvoices = ref([])

const invoiceData = ref({
  clientName: '',
  amount: '',
  description: '',
  dueDate: '',
  paymentTerms: '',
  notes: ''
})

const paymentData = ref({
  amount: '',
  paymentMethod: '',
  clientName: '',
  description: '',
  paymentDate: '',
  referenceNumber: ''
})

// Computed
const filteredPayments = computed(() => {
  let filtered = payments.value

  if (searchTerm.value) {
    const search = searchTerm.value.toLowerCase()
    filtered = filtered.filter(payment => 
      payment.description.toLowerCase().includes(search) ||
      payment.client.toLowerCase().includes(search) ||
      payment.paymentMethod.toLowerCase().includes(search)
    )
  }

  if (filterStatus.value) {
    filtered = filtered.filter(payment => payment.status === filterStatus.value)
  }

  return filtered
})

const paymentSummary = computed(() => {
  const totalReceived = payments.value
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + p.amount, 0)
  
  const pending = payments.value
    .filter(p => p.status === 'pending')
    .reduce((sum, p) => sum + p.amount, 0)
  
  const overdue = recentInvoices.value
    .filter(i => i.status === 'overdue')
    .reduce((sum, i) => sum + i.amount, 0)
  
  return {
    totalReceived,
    pending,
    overdue,
    netRevenue: totalReceived - pending
  }
})

// Methods
const fetchPaymentStats = async () => {
  try {
    const response = await fetch('/api/company/payment-stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      paymentStats.value = data
    }
  } catch (error) {
    console.error('Error fetching payment stats:', error)
  }
}

const fetchPayments = async () => {
  try {
    const response = await fetch('/api/company/payments', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      payments.value = data.payments || []
    }
  } catch (error) {
    console.error('Error fetching payments:', error)
  }
}

const fetchRecentInvoices = async () => {
  try {
    const response = await fetch('/api/company/recent-invoices', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      recentInvoices.value = data.invoices || []
    }
  } catch (error) {
    console.error('Error fetching recent invoices:', error)
  }
}

const submitInvoice = async () => {
  try {
    const response = await fetch('/api/company/create-invoice', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(invoiceData.value)
    })
    
    if (response.ok) {
      showCreateInvoiceModal.value = false
      invoiceData.value = { clientName: '', amount: '', description: '', dueDate: '', paymentTerms: '', notes: '' }
      await fetchRecentInvoices()
      await fetchPaymentStats()
    }
  } catch (error) {
    console.error('Error creating invoice:', error)
  }
}

const submitPayment = async () => {
  try {
    const response = await fetch('/api/company/record-payment', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(paymentData.value)
    })
    
    if (response.ok) {
      showPaymentModal.value = false
      paymentData.value = { amount: '', paymentMethod: '', clientName: '', description: '', paymentDate: '', referenceNumber: '' }
      await fetchPayments()
      await fetchPaymentStats()
    }
  } catch (error) {
    console.error('Error recording payment:', error)
  }
}

const viewPaymentDetails = (payment: any) => {
  // Navigate to payment details page
}

const downloadReceipt = (paymentId: number) => {
  // Download receipt
}

const loadCompanyInvoice = async (invoiceId: number) => {
  const response = await fetch(`/api/company/invoices/${invoiceId}`, {
    headers: {
      'Authorization': `Bearer ${userStore.authToken}`,
      'Content-Type': 'application/json'
    }
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data.error || 'Invoice not found')
  return data.invoice
}

const viewInvoiceDetails = async (invoice: any) => {
  try {
    viewingInvoice.value = await loadCompanyInvoice(invoice.id)
  } catch (error) {
    console.error('Error loading invoice:', error)
  }
}

const downloadInvoice = async (invoiceId: number) => {
  try {
    viewingInvoice.value = await loadCompanyInvoice(invoiceId)
    setTimeout(() => window.print(), 150)
  } catch (error) {
    console.error('Error loading invoice:', error)
  }
}

const printViewingInvoice = () => {
  window.print()
}

const sendInvoiceReminder = async (invoiceId: number) => {
  try {
    const response = await fetch('/api/company/send-invoice-reminder', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ invoiceId })
    })
    
    if (response.ok) {
      // Show success message
    }
  } catch (error) {
    console.error('Error sending invoice reminder:', error)
  }
}

const voidInvoice = async (invoice: any) => {
  if (!window.confirm(`Void invoice ${invoice.invoiceNumber}? The record will remain for audit history.`)) return
  try {
    const response = await fetch(`/api/company/void-invoice/${invoice.id}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || 'Void failed')
    await fetchRecentInvoices()
    await fetchPaymentStats()
  } catch (error) {
    console.error('Error voiding invoice:', error)
  }
}

const generatePaymentReport = () => {
  // Generate payment report
}

const sendPaymentReminders = () => {
  // Send payment reminders
}

const exportPaymentData = () => {
  const csvContent = [
    ['Date', 'Client', 'Description', 'Amount', 'Status', 'Payment Method'],
    ...filteredPayments.value.map(payment => [
      formatDate(payment.date),
      payment.client,
      payment.description,
      `$${payment.amount.toLocaleString()}`,
      payment.status,
      payment.paymentMethod
    ])
  ].map(row => row.join(',')).join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'company-payments.csv'
  a.click()
  window.URL.revokeObjectURL(url)
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchPaymentStats(),
    fetchPayments(),
    fetchRecentInvoices()
  ])
})
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}

.quick-action-card {
  @apply flex flex-col items-center justify-center p-4 bg-slate-700/30 rounded-xl hover:bg-slate-600/50 transition-colors;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}

@media print {
  :global(body *) { visibility: hidden; }
  .invoice-view, .invoice-view * { visibility: visible; }
  .invoice-view {
    position: static !important;
    background: #fff;
    display: block;
    padding: 0;
  }
}
</style>
